import { Box, Grid, Typography, Menu, MenuItem } from '@mui/material';
import { useState, useEffect, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useSelector } from '../../redux/store';
import { CustomAvatar } from '../custom-avatar';
import Markdown from '../markdown';
import InvoiceFileAttachmentRow from '../invoice-file-attachment/InvoiceFileAttachmentRow';
import { downloadInvoiceFile } from '../../api/invoicing';
import { HOST_API_URL } from '../../config-global';
import { stringToColor } from '../../utils/cssStyles';
import { fMonthDayTimeYear } from '../../utils/formatTime';
import { replaceLinks, cleanMailTo } from '../markdown/markdown-cleanup';
import Iconify from '../iconify';
import {
  getInvoiceFileExtension,
  isPreviewableInvoiceFile,
  runInvoiceAttachmentHrefAction,
} from '../../utils/helpers/invoiceFileActions';

// ----------------------------------------------------------------------

MessageItem.propTypes = {
  message: PropTypes.object,
  onReply: PropTypes.func,
  reply: PropTypes.object,
  messages: PropTypes.array
};

const sanitizeChatMessageHtml = (html = '') => {
  if (!html?.includes('reply-preview')) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('.reply-preview').forEach((preview) => preview.remove());
  return doc.body.innerHTML;
};

const mapAttachmentLinks = (attachments = []) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return [];

  return attachments.reduce((links, attachment) => {
    const attachmentId = Number(attachment?.id);
    if (!Number.isFinite(attachmentId)) return links;

    const extension = String(attachment?.fileExtension || '').trim();
    const baseName = String(attachment?.fileName || '').trim();
    const fileName = extension && !baseName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)
      ? `${baseName}.${extension}`
      : baseName || 'Attachment';

    links.push({
      href: `${HOST_API_URL}Api/Invoices/DownloadInvoiceFile?id=${attachmentId}&mainFile=false`,
      attachmentId,
      fileName,
    });

    return links;
  }, []);
};

export default function MessageItem({
  message,
  onReply,
  reply,
  messages
}) {

  const { fullName, note, sentOn, sentBy } = message;
  const { email } = useSelector((state) => state.user.user);
  const currentUser = sentBy === email;
  const [replyMenuAnchorEl, setReplyMenuAnchorEl] = useState(null);

  const parentMessage = message.parentCommentId ? messages.find(msg => msg.id === message.parentCommentId) : null;
  const parentMessageNote = parentMessage?.note ?? parentMessage?.extraInfo ?? '';
  const parentMessageSender = parentMessage?.fullName ?? parentMessage?.user?.fullName ?? '';
  const parentMessageSentBy = parentMessage?.sentBy ?? parentMessage?.user?.email ?? '';
  const parentMessageAttachments = parentMessage?.attachments ?? [];

  const scrollToParentMessage = () => {

    if (parentMessage) {
      const element = document.getElementById(`${parentMessage.id}-message-box`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    else {
      enqueueSnackbar("Original message not found", { variant: "info" });
    }
  };

  const boxRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const previewContainer = target.closest('.reply-preview, .chat-reply-preview');
      if (previewContainer && boxRef.current?.contains(previewContainer)) {
        scrollToParentMessage();
      }
    };
    const box = boxRef.current;
    if (box) box.addEventListener('click', handler);
    return () => {
      if (box) box.removeEventListener('click', handler);
    };
  }, [parentMessage]);

  const messageContent = useMemo(() => sanitizeChatMessageHtml(note || ''), [note]);
  const parentMessageContent = useMemo(() => sanitizeChatMessageHtml(parentMessageNote), [parentMessageNote]);

  const attachmentLinks = useMemo(() => mapAttachmentLinks(message?.attachments), [message?.attachments]);
  const parentAttachmentLinks = useMemo(() => mapAttachmentLinks(parentMessageAttachments), [parentMessageAttachments]);

  const openAttachment = (href, fileName) =>
    runInvoiceAttachmentHrefAction({
      href,
      fileName,
      action: 'open',
      downloadInvoiceFileApi: downloadInvoiceFile,
    });

  const downloadAttachment = (href, closeMenu) =>
    runInvoiceAttachmentHrefAction({
      href,
      action: 'download',
    }).finally(() => closeMenu?.());

  return (
    <Grid
      container
      direction="row"
      justifyContent={currentUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-end"
      sx={{
        mt: 2,
        p: 1,
        boxSizing: 'border-box',
        pr: currentUser ? 2 : 0
      }}
    >
      <Grid container direction="row" sx={{ width: '100%', boxSizing: 'border-box' }} spacing={1}>
        {!currentUser &&
          <Grid item sx={{ mt: 'auto' }}>
            <CustomAvatar
              name={fullName}
              email={sentBy}
              showTooltip
              sx={{ opacity: 1 }}
            />
          </Grid>
        }
        <Grid item sx={{ flex: 1, minWidth: 0 }}>
        <Box
          id={`${message.id}-message-box`}
          ref={boxRef}
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            borderRadius: currentUser  ? '20px 20px 0 20px' : '20px 20px 20px 0' ,
            wordWrap: 'break-word',
            typography: 'body2',
            p: 1,
            border: `1px solid ${stringToColor(sentBy)}`,
            backgroundColor: () => `${alpha(stringToColor(sentBy), 0.05)}`,
            display: 'inline-block',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            '& img': {
              cursor: 'pointer',
              maxWidth: '100%',
              objectFit: 'contain',
              maxHeight: '200px',
              '&:hover': {
                filter: 'brightness(0.9)',
                padding: 0.3,
              },
            },
          }}
        >
          {onReply && !reply?.id && <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Iconify icon="mdi:dots-horizontal" width={16} height={16} onClick={(e) => setReplyMenuAnchorEl(e.currentTarget)}/>
          </Box>}
          <Menu
            anchorEl={replyMenuAnchorEl}
            open={Boolean(replyMenuAnchorEl)}
            onClose={() => setReplyMenuAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                onReply(message.note, message.fullName, message.id);
                setReplyMenuAnchorEl(null);
              }
            }>
              Reply
            </MenuItem>
          </Menu>
          {parentMessage && (
            <Box
              className="chat-reply-preview"
              sx={{
                borderLeft: `3px solid ${stringToColor(parentMessageSentBy)}`,
                backgroundColor: () => `${alpha(stringToColor(parentMessageSentBy), 0.08)}`,
                borderRadius: 1,
                p: 1,
                mb: 1,
                cursor: 'pointer',
                display: 'block',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {parentMessageSender}
              </Typography>
              <Markdown
                children={cleanMailTo(replaceLinks(parentMessageContent))}
                sx={{
                  mt: 0.25,
                  '& p': { m: 0 },
                }}
              />
              {parentAttachmentLinks.length > 0 && (
                <Box
                  sx={{ mt: 0.5, maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  {parentAttachmentLinks.map((attachment) => {
                    const previewable = isPreviewableInvoiceFile({ fileName: attachment.fileName });
                    return (
                      <InvoiceFileAttachmentRow
                        key={`parent-${message.id}-${attachment.href}-${attachment.fileName}`}
                        compact
                        file={{ name: attachment.fileName, fileExtension: getInvoiceFileExtension(attachment.fileName) }}
                        fileName={attachment.fileName}
                        onOpen={previewable ? () => openAttachment(attachment.href, attachment.fileName) : undefined}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
          <Markdown
            children={cleanMailTo(replaceLinks(messageContent))}
            sx={{
              '& blockquote': {
                borderLeft: '2px solid black',
                fontSize: '0.875rem',
                margin: 0,
                borderRadius: 0,
                fontFamily: 'Public Sans,sans-serif',
                padding: 0,
                paddingLeft: '6px',
                backgroundColor: alpha(stringToColor(sentBy), 0.05),                
                marginLeft: 1,
                color: 'black !important',
                '&:before': {
                  content: 'none',
                },
              },
            }}
          />
          {attachmentLinks.length > 0 && (
            <Box sx={{ mt: 0.5, maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
              {attachmentLinks.map((attachment) => {
                const previewable = isPreviewableInvoiceFile({ fileName: attachment.fileName });
                return (
                <InvoiceFileAttachmentRow
                  key={`${message.id}-${attachment.href}-${attachment.fileName}`}
                  file={{ name: attachment.fileName, fileExtension: getInvoiceFileExtension(attachment.fileName) }}
                  fileName={attachment.fileName}
                  onOpen={previewable ? () => openAttachment(attachment.href, attachment.fileName) : undefined}
                  onDownload={(closeMenu) => downloadAttachment(attachment.href, closeMenu)}
                />
                );
              })}
            </Box>
          )}
        </Box>
        <Grid
          direction="row"
          alignItems="center"
          container
          sx={{ display: 'flex', justifyContent: currentUser ? 'right' : 'left' }}
        >
          <Typography noWrap variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }} component="span">
            {fullName} {fMonthDayTimeYear(sentOn)}
          </Typography>
          </Grid>
        </Grid>
        {currentUser &&
          <Grid item sx={{ mt: 'auto' }}>
            <CustomAvatar
              name={fullName}
              email={sentBy}
              showTooltip
              sx={{ opacity: 1 }}
            />
          </Grid>
        }
      </Grid>
    </Grid>
  );
}
